import { useState } from "react";
import { useForm } from "react-hook-form";

import { ApiError } from "@/api/client";
import { useSaveContact, type ContactInput } from "@/api/hooks";
import type { Contact, CustomValues } from "@/api/types";
import { CustomFieldsFieldset } from "@/components/crm/CustomFieldsFieldset";
import { RecordPicker, type PickerValue } from "@/components/crm/RecordPicker";
import { Alert, TextField } from "@/components/ui";

type Values = Omit<ContactInput, "custom">;
const STANDARD: (keyof Values)[] = ["first_name", "last_name", "email", "phone", "job_title", "company_name"];

const toNull = (v: unknown) => (typeof v === "string" && v.trim() === "" ? null : v);

export function ContactForm({ contact, defaultAccount, onSaved, onCancel }: {
  contact?: Contact; defaultAccount?: PickerValue; onSaved: (c: Contact) => void; onCancel?: () => void;
}) {
  const [account, setAccount] = useState<PickerValue | null>(contact?.account ?? defaultAccount ?? null);
  const save = useSaveContact(contact?.id);
  const [custom, setCustom] = useState<CustomValues>(contact?.custom ?? {});
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const { register, handleSubmit, formState } = useForm<Values>({
    defaultValues: Object.fromEntries(STANDARD.map((k) => [k, contact?.[k] ?? ""])) as Values,
  });

  const onSubmit = handleSubmit(async (values) => {
    setServerErrors({}); setFormError(null);
    const body: ContactInput & { account_id: string | null } = { first_name: (values.first_name ?? "").trim(), custom, account_id: account?.id ?? null };
    for (const k of STANDARD.slice(1)) (body as unknown as Record<string, unknown>)[k] = toNull(values[k]);
    try {
      onSaved(await save.mutateAsync(body));
    } catch (e) {
      if (e instanceof ApiError && e.problem.errors?.length) setServerErrors(e.fieldErrors());
      else setFormError(e instanceof Error ? e.message : "Could not save");
    }
  });

  const err = (k: string) => serverErrors[k] ?? (formState.errors as Record<string, { message?: string }>)[k]?.message;

  return (
    <form onSubmit={onSubmit} className="space-y-6" noValidate>
      {formError && <Alert>{formError}</Alert>}
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField label="First name *" {...register("first_name", { required: "First name is required" })} error={err("first_name")} />
        <TextField label="Last name" {...register("last_name")} error={err("last_name")} />
        <TextField label="Email" type="email" {...register("email")} error={err("email")} />
        <TextField label="Phone" type="tel" placeholder="+91 98200 12345" {...register("phone")} error={err("phone")} />
        <TextField label="Job title" {...register("job_title")} error={err("job_title")} />
        <TextField label="Company" {...register("company_name")} error={err("company_name")} />
        <div className="sm:col-span-2"><RecordPicker label="Account" endpoint="/accounts" value={account} onChange={setAccount} error={err("account_id")} /></div>
      </div>
      <CustomFieldsFieldset objectType="contact" value={custom} onChange={setCustom} errors={serverErrors} />
      <div className="flex justify-end gap-2">
        {onCancel && <button type="button" className="btn-ghost" onClick={onCancel}>Cancel</button>}
        <button type="submit" className="btn-primary" disabled={save.isPending}>
          {save.isPending ? "Saving…" : contact ? "Save changes" : "Create contact"}
        </button>
      </div>
    </form>
  );
}
